import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { usePrefs } from "@/contexts/PrefsContext";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { strings } from "@/i18n/strings";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import { SUPPORTED_DISPLAY_CURRENCIES } from "@/lib/format";
import { useCountryList } from "@/components/CountryPicker";
import { CountrySelect } from "@/components/GlobalLocationFields";
import ConnectedAccountsCard from "@/components/settings/ConnectedAccountsCard";
import ChangePasswordCard from "@/components/settings/ChangePasswordCard";
import NotificationsCard from "@/components/settings/NotificationsCard";
import { Check, LogOut, Sparkles, Globe, Coins, MapPin, Ruler, Gift, Copy, Filter, Briefcase, Home as HomeIcon } from "lucide-react";
import PurchaseCriteriaSection from "@/components/settings/PurchaseCriteriaSection";

const PLAN_SET = strings.settings.plans;

const PLANS = [
  { key: "free", ...PLAN_SET.free, recommended: false },
  { key: "pro", ...PLAN_SET.pro, recommended: true },
  { key: "enterprise", ...PLAN_SET.enterprise, recommended: false },
];

export default function Settings() {
  const { user, logout } = useAuth();
  const { language, displayCurrency, defaultCountry, setLanguage, setDisplayCurrency, setDefaultCountry } = usePrefs();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const countries = useCountryList();
  const currentPlan = user?.subscription_plan || "free";
  const [aiStatus, setAiStatus] = useState(null);
  const [unitSystem, setUnitSystem] = useState(user?.unit_system || "imperial");
  // Phase 36 — default analysis role (Investor / Owner). Pre-fills the
  // wizard for new deals; per-deal role can still override.
  const [defaultRole, setDefaultRole] = useState(
    (user?.default_analysis_role || "buyer").toLowerCase(),
  );
  // Phase 23 — referral state (lazy-fetched on mount)
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/ai/status").then(({ data }) => setAiStatus(data)).catch(() => setAiStatus({ enabled: false }));
  }, []);

  useEffect(() => {
    api.get("/users/me/referral")
      .then(({ data }) => setReferral(data))
      .catch(() => setReferral(null));
  }, []);

  const onCopyReferral = async () => {
    if (!referral?.referral_url) return;
    try {
      await navigator.clipboard.writeText(referral.referral_url);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Could not copy — select the text manually.");
    }
  };

  useEffect(() => {
    if (user?.unit_system) setUnitSystem(user.unit_system);
  }, [user]);

  // Phase 36 — keep local default-role in sync with the user doc so
  // logging out + back in / switching accounts shows the right radio.
  useEffect(() => {
    if (user?.default_analysis_role) setDefaultRole(user.default_analysis_role.toLowerCase());
  }, [user]);

  const onDefaultRoleChange = async (v) => {
    setDefaultRole(v);
    try {
      await api.patch("/auth/me", { default_analysis_role: v });
      toast.success("Default analysis role updated.");
    } catch (e) {
      toast.error("Could not save default role.");
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const onLangChange = async (code) => {
    await setLanguage(code);
    toast.success(t("settings.preferences_saved"));
  };
  const onCurrencyChange = async (code) => {
    await setDisplayCurrency(code);
    toast.success(t("settings.preferences_saved"));
  };
  const onCountryChange = async (code) => {
    await setDefaultCountry(code);
    toast.success(t("settings.preferences_saved"));
  };
  const onUnitSystemChange = async (v) => {
    setUnitSystem(v);
    try {
      await api.patch("/auth/me", { unit_system: v });
      toast.success(t("settings.preferences_saved"));
    } catch (e) {
      toast.error("Could not save unit system");
    }
  };

  return (
    <AppShell>
      <div className="space-y-10 fade-in" data-testid="settings-root">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-indigo-600">
            {t("nav.settings")}
          </p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("settings.title")}
          </h1>
        </div>

        {/* Profile */}
        <section
          data-testid="profile-section"
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
        >
          <header className="mb-6">
            <h2 className="text-lg font-semibold text-slate-900">{t("settings.profile")}</h2>
            <p className="mt-1 text-sm text-slate-500">
              Your basic account information.
            </p>
          </header>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <Label className="text-slate-700">{t("settings.name_label")}</Label>
              <Input
                value={user?.name || ""}
                readOnly
                data-testid="profile-name"
                className="mt-1.5 bg-slate-50"
              />
            </div>
            <div>
              <Label className="text-slate-700">{t("settings.email_label")}</Label>
              <Input
                value={user?.email || ""}
                readOnly
                data-testid="profile-email"
                className="mt-1.5 bg-slate-50"
              />
            </div>
          </div>
        </section>

        {/* Phase 36 Batch S — Connected accounts (Google Sign-In) */}
        <ConnectedAccountsCard />

        {/* Phase 36 Batch U — Self-service Change Password */}
        <ChangePasswordCard />

        {/* Phase 40 Batch BD — Email notification preferences */}
        <NotificationsCard />

        {/* Preferences — language / currency / default country */}
        <section
          data-testid="preferences-section"
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
        >
          <header className="mb-6">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
              <Globe className="h-4 w-4 text-indigo-600" /> Workspace preferences
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Language, currency and default country apply across the whole app.
            </p>
          </header>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label className="flex items-center gap-1.5 text-slate-700">
                <Globe className="h-3.5 w-3.5 text-slate-400" /> {t("settings.language_label")}
              </Label>
              <Select value={language} onValueChange={onLangChange}>
                <SelectTrigger data-testid="pref-language" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_LANGUAGES.map((l) => (
                    <SelectItem key={l.code} value={l.code} data-testid={`pref-language-${l.code}`}>
                      {l.nativeName} <span className="ms-2 text-xs text-slate-400">{l.code}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="flex items-center gap-1.5 text-slate-700">
                <Coins className="h-3.5 w-3.5 text-slate-400" /> {t("settings.display_currency_label")}
              </Label>
              <Select value={displayCurrency} onValueChange={onCurrencyChange}>
                <SelectTrigger data-testid="pref-currency" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_DISPLAY_CURRENCIES.map((c) => (
                    <SelectItem key={c.code} value={c.code} data-testid={`pref-currency-${c.code}`}>
                      {c.code} — {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-slate-500">
                Stored values stay in USD; display converts at FX.
              </p>
            </div>
            <div>
              <Label className="flex items-center gap-1.5 text-slate-700">
                <MapPin className="h-3.5 w-3.5 text-slate-400" /> {t("settings.default_country_label")}
              </Label>
              {/* Phase 36 Batch R — global country list (~243 entries)
                  via /api/locations. Pre-fills the wizard country on
                  every new deal. */}
              <div className="mt-1.5">
                <CountrySelect
                  value={defaultCountry || "US"}
                  onChange={onCountryChange}
                  testid="pref-country"
                />
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Pre-fills the New Deal wizard.
              </p>
            </div>
            <div>
              <Label className="flex items-center gap-1.5 text-slate-700">
                <Ruler className="h-3.5 w-3.5 text-slate-400" /> Measurement system
              </Label>
              <Select value={unitSystem} onValueChange={onUnitSystemChange}>
                <SelectTrigger data-testid="pref-unit-system" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="imperial" data-testid="pref-unit-imperial">Imperial (ft²)</SelectItem>
                  <SelectItem value="metric" data-testid="pref-unit-metric">Metric (m²)</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-slate-500">Area display + AI narratives.</p>
            </div>
          </div>
        </section>

        {/* AI Service Status */}
        <section
          data-testid="ai-status-section"
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                <Sparkles className="h-4 w-4 text-indigo-600" /> AI service
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                {aiStatus?.enabled
                  ? `Connected — ${aiStatus.model_analyst} (Analyst) · ${aiStatus.model_copilot} (Copilot)`
                  : "Limited — AI is currently unavailable, showing baseline (deterministic) analysis."}
              </p>
            </div>
            <span
              data-testid="ai-status-pill"
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider ${
                aiStatus?.enabled
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-amber-50 text-amber-800"
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${aiStatus?.enabled ? "bg-emerald-500" : "bg-amber-500"}`} />
              {aiStatus?.enabled ? "Connected" : "Limited"}
            </span>
          </div>
        </section>

        {/* Phase 36 — Default analysis role (Investor / Owner) */}
        <section
          data-testid="default-role-section"
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
        >
          <header className="mb-5 flex items-start gap-3">
            <div className="rounded-lg bg-indigo-100 p-2 text-indigo-700">
              <Briefcase className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Default analysis role
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                What perspective do you usually analyse from? This pre-fills
                the Deal Analyzer; you can still flip the role on any
                individual deal.
              </p>
            </div>
          </header>
          <div role="radiogroup" aria-label="Default analysis role" className="grid gap-3 sm:grid-cols-2">
            {[
              {
                value: "buyer",
                label: "Investor (Buyer side)",
                hint: "I'm analysing properties I might buy. Math anchors to my offer / counter.",
                Icon: Briefcase,
                tone: "indigo",
              },
              {
                value: "seller",
                label: "Owner (Seller side)",
                hint: "I'm listing or about to list. Math anchors to my asking price.",
                Icon: HomeIcon,
                tone: "amber",
              },
            ].map((opt) => {
              const active = defaultRole === opt.value;
              const ring = active
                ? opt.tone === "amber"
                  ? "ring-2 ring-amber-400 bg-amber-50/50"
                  : "ring-2 ring-indigo-400 bg-indigo-50/50"
                : "border-slate-200 hover:border-slate-300 hover:shadow-sm";
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  data-testid={`default-role-${opt.value}`}
                  onClick={() => onDefaultRoleChange(opt.value)}
                  className={`flex items-start gap-3 rounded-xl border bg-white p-4 text-left transition-all ${ring}`}
                >
                  <opt.Icon
                    className={`mt-0.5 h-5 w-5 flex-shrink-0 ${
                      opt.tone === "amber" ? "text-amber-600" : "text-indigo-600"
                    }`}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900">{opt.label}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{opt.hint}</p>
                  </div>
                  {active ? <Check className="h-4 w-4 flex-shrink-0 text-slate-500" /> : null}
                </button>
              );
            })}
          </div>
        </section>

        {/* Batch H — Purchase Criteria (DealCheck-style rules + thresholds) */}
        <section
          id="criteria"
          data-testid="purchase-criteria-anchor"
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
        >
          <header className="mb-5 flex items-start gap-3">
            <div className="rounded-lg bg-indigo-100 p-2 text-indigo-700">
              <Filter className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Purchase criteria
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Define what makes a deal a "buy" for you. Every deal you
                analyse is auto-checked against these rules — see the
                breakdown on the Deal Detail page's <em>Criteria</em> tab.
              </p>
            </div>
          </header>
          <PurchaseCriteriaSection />
        </section>

        {/* Subscription */}
        <section
          data-testid="subscription-section"
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
        >
          <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{t("settings.plans_title")}</h2>
              <p className="mt-1 text-sm text-slate-500">
                {t("settings.plans_footer")}
              </p>
            </div>
            <span
              data-testid="current-plan-badge"
              className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-indigo-700"
            >
              Current: {currentPlan}
            </span>
          </header>

          <div className="grid gap-4 lg:grid-cols-3">
            {PLANS.map((plan) => {
              const active = plan.key === currentPlan;
              const isRecommended = plan.recommended;
              const onClick = () => {
                if (plan.key === "free") return;
                toast(t("common.coming_soon_billing"));
              };
              return (
                <div
                  key={plan.key}
                  data-testid={`plan-card-${plan.key}`}
                  className={`relative flex flex-col rounded-xl border p-6 transition-all ${
                    isRecommended
                      ? "border-indigo-300 bg-white shadow-md ring-2 ring-indigo-100"
                      : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
                  }`}
                >
                  {isRecommended && (
                    <span className="absolute -top-2.5 left-6 inline-flex items-center gap-1 rounded-full bg-indigo-600 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white shadow-sm">
                      {t("settings.plans.recommended")}
                    </span>
                  )}
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-slate-900">{plan.name}</h3>
                    {active && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                        Active
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="text-3xl font-bold tracking-tight text-slate-900">{plan.price}</span>
                    {plan.period && <span className="text-sm text-slate-500">{plan.period}</span>}
                  </div>
                  <ul className="mt-5 flex-1 space-y-2 text-sm text-slate-600">
                    {plan.features.map((p) => (
                      <li key={p} className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-600" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6">
                    <Button
                      disabled={plan.key === "free"}
                      onClick={onClick}
                      data-testid={`plan-cta-${plan.key}`}
                      variant={isRecommended ? "default" : "outline"}
                      className={`w-full active:scale-[0.98] ${
                        isRecommended
                          ? "bg-indigo-600 text-white hover:bg-indigo-700"
                          : "border-slate-200 text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {plan.key === "free" ? plan.cta_current : plan.cta}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Phase 23 — Refer Friends */}
        <section
          data-testid="referral-section"
          className="relative overflow-hidden rounded-2xl border border-emerald-200/70 bg-gradient-to-br from-emerald-50 via-white to-white p-6 shadow-sm sm:p-8"
        >
          <div className="absolute -right-12 -top-12 h-44 w-44 rounded-full bg-emerald-200/40 blur-3xl" aria-hidden="true" />
          <header className="relative mb-6">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
              <Gift className="h-5 w-5 text-emerald-600" />
              <span data-testid="referral-heading">Refer Friends &amp; Get Pro Free</span>
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              Each friend who joins via your link gets 1 month Pro free — and so do you. Forever.
            </p>
          </header>

          {referral ? (
            <div className="relative space-y-5">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Input
                  readOnly
                  value={referral.referral_url}
                  data-testid="referral-link-input"
                  onFocus={(e) => e.target.select()}
                  className="bg-white/80 font-mono text-sm"
                />
                <Button
                  type="button"
                  onClick={onCopyReferral}
                  data-testid="referral-copy-button"
                  className="gap-2 bg-emerald-600 text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-emerald-700 hover:shadow-md"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? "Copied" : "Copy Link"}
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div data-testid="referral-friends-joined" className="rounded-full bg-white/80 px-3 py-1 font-medium text-slate-700 ring-1 ring-emerald-200">
                  <span className="font-bold tabular-nums text-emerald-700">{referral.stats?.referrals_count ?? 0}</span>{" "}
                  friends joined
                </div>
                <span className="text-slate-300">·</span>
                <div data-testid="referral-months-earned" className="rounded-full bg-white/80 px-3 py-1 font-medium text-slate-700 ring-1 ring-emerald-200">
                  <span className="font-bold tabular-nums text-emerald-700">{referral.stats?.pro_months_earned ?? 0}</span>{" "}
                  month{referral.stats?.pro_months_earned === 1 ? "" : "s"} Pro earned
                </div>
                {referral.stats?.pro_expires_at && (
                  <>
                    <span className="text-slate-300">·</span>
                    <div data-testid="referral-pro-expires" className="rounded-full bg-indigo-50 px-3 py-1 font-medium text-indigo-700 ring-1 ring-indigo-200">
                      Pro expires: {new Date(referral.stats.pro_expires_at).toLocaleDateString()}
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500" data-testid="referral-loading">Generating your unique link…</p>
          )}
        </section>

        {/* Danger / logout */}
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Session</h2>
              <p className="mt-1 text-sm text-slate-500">
                Log out of this device. You can sign back in any time.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={onLogout}
              data-testid="settings-logout-button"
              className="border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              <LogOut className="me-2 h-4 w-4" /> {t("nav.logout")}
            </Button>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
